import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2, ArrowRight, GraduationCap } from "lucide-react";

const COACH_AVATAR = "https://static.prod-images.emergentagent.com/jobs/1a9e1561-ca18-4dbb-8f52-13e836a03554/images/063325646653cf8a151ee440b3e604a7efe09ddef4648003466f156b1d0b378f.jpeg";
const clean = (s) => (s || "").replace(/[*_#`>]+/g, "");

export default function CoachShare() {
  const { id } = useParams();
  const [data, setData] = useState(undefined);
  useEffect(() => { api.get(`/coach-share/${id}`).then((r) => setData(r.data)).catch(() => setData(null)); }, [id]);
  if (data === undefined) return <div className="min-h-screen flex items-center justify-center text-emerald-600" style={{ background: "#F4F7FB" }}><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="min-h-screen grid-bg text-slate-900" style={{ background: "#F4F7FB" }} data-testid="coach-share-page">
      <header className="max-w-3xl mx-auto px-6 pt-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2"><div className="w-9 h-9 rounded-xl bg-emerald-500 flex items-center justify-center glow-emerald"><Sparkles className="w-5 h-5 text-black" /></div><span className="font-display font-extrabold">Pitch Craft</span></Link>
        <Link to="/register"><Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold" data-testid="coach-share-cta-top">Try it free</Button></Link>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-10 space-y-6">
        {!data ? (
          <div className="card-surface rounded-3xl p-10 text-center" data-testid="coach-share-missing">
            <h1 className="text-2xl font-bold">This coaching session isn't available</h1>
            <p className="text-slate-500 mt-2">The link may have been removed by its owner.</p>
          </div>
        ) : (
          <>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="card-surface rounded-3xl p-6 sm:p-8 flex items-center gap-4 flex-wrap">
              <img src={COACH_AVATAR} alt="Linden" className="w-16 h-16 rounded-full object-cover object-top border-2 border-white shadow" />
              <div className="min-w-0">
                <p className="text-emerald-600 font-mono text-xs uppercase tracking-widest">Shared coaching session</p>
                <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight" data-testid="coach-share-title">{data.rep_name} × Linden, AI sales coach</h1>
                <p className="text-slate-500 text-sm">{new Date(data.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} · {data.messages.length} messages</p>
              </div>
            </motion.div>
            <div className="card-surface rounded-2xl p-5 space-y-3" data-testid="coach-share-transcript">
              {data.messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} items-end gap-2`}>
                  {m.role !== "user" && <img src={COACH_AVATAR} alt="" className="w-7 h-7 rounded-full object-cover object-top shrink-0" />}
                  <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-emerald-500 text-black" : "bg-slate-100 text-slate-800"}`}>
                    <div className="text-[10px] font-mono uppercase tracking-wider opacity-60 mb-0.5">{m.role === "user" ? data.rep_name : "Linden"}</div>
                    {clean(m.content)}
                  </div>
                </div>
              ))}
            </div>
            <div className="rounded-3xl p-8 text-center bg-slate-900 text-white">
              <GraduationCap className="w-8 h-8 mx-auto text-emerald-400" />
              <h2 className="text-xl sm:text-2xl font-extrabold mt-3">Want a sales coach in your pocket?</h2>
              <p className="text-slate-300 mt-2 text-sm">Linden coaches you after every practice call and answers any sales question, any time.</p>
              <Link to="/register"><Button className="mt-5 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold h-11 px-6" data-testid="coach-share-cta-bottom">Try Pitch Craft free <ArrowRight className="w-4 h-4 ml-1.5" /></Button></Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
